import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, UserPlus, CalendarPlus, Receipt, BadgeCheck, Flame, ShieldCheck, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import api from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DotCoinIcon from "../components/DotCoinIcon";
import { useDotCoins } from "../contexts/DotCoinsContext";

const RULE_ICONS = {
  daily_login: CalendarCheck, patient_created: UserPlus, appointment_created: CalendarPlus,
  bill_created: Receipt, profile_complete: BadgeCheck, weekly_bonus: Flame, admin_adjustment: ShieldCheck,
};
const FIELD_LABELS = { name: "Your name", clinic_name: "Clinic name", clinic_address: "Clinic address", phone: "Phone", clinic_logo: "Clinic logo" };

const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const BalanceTab = ({ s }) => {
  const pct = Math.min(100, Math.round((s.week_actions / s.weekly_threshold) * 100));
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="relative overflow-hidden rounded-2xl bg-[#0F2A5F] p-8 text-white lg:col-span-2" data-testid="dotcoins-balance-card">
        <div className="absolute -right-10 -top-10 opacity-20"><DotCoinIcon size={220} /></div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5EEAD4]">Available balance</div>
        <div className="mt-3 flex items-end gap-3">
          <span className="text-6xl font-bold tabular-nums" data-testid="dotcoins-balance-value">{s.balance}</span>
          <span className="pb-2 text-sm text-white/70">DotCoins</span>
        </div>
        <div className="mt-8 flex gap-10 text-sm">
          <div><div className="text-white/60">Total earned</div><div className="text-xl font-semibold" data-testid="dotcoins-total-earned">{s.total_earned}</div></div>
          <div><div className="text-white/60">Redeemed</div><div className="text-xl font-semibold" data-testid="dotcoins-total-spent">{s.total_spent}</div></div>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="dotcoins-weekly-progress">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Flame size={16} className="text-[#14B8A6]" /> Weekly streak</div>
        <p className="mt-1 text-xs text-slate-500">Do {s.weekly_threshold} rewarded actions in 7 days for a bonus.</p>
        <div className="mt-5 h-2.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-[#14B8A6] transition-[width] duration-700" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2 text-sm text-slate-700"><b>{Math.min(s.week_actions, s.weekly_threshold)}</b> / {s.weekly_threshold} actions</div>
        <div className={`mt-4 rounded-md px-3 py-2 text-xs font-medium ${s.weekly_bonus_earned ? "bg-[#14B8A6]/10 text-[#0F8A7D]" : "bg-slate-50 text-slate-600"}`}>
          {s.weekly_bonus_earned ? "Bonus earned this week!" : "Bonus not yet earned this week"}
        </div>
      </div>
    </div>
  );
};

const HistoryTab = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dotcoins/history?limit=200").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-center text-sm text-slate-400">Loading...</div>;
  if (!data.items.length) return <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid="dotcoins-history-empty">No transactions yet. Start earning from the Earn tab.</div>;
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="dotcoins-history-list">
      {data.items.map((i) => {
        const Icon = RULE_ICONS[i.rule_key] || DotCoinIcon;
        const pos = i.amount > 0;
        return (
          <div key={i.id} className="flex items-center gap-4 border-b border-slate-100 px-5 py-3.5 last:border-0" data-testid="dotcoins-history-item">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-[#0F2A5F]"><Icon size={16} /></div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-800">{i.description}</div>
              <div className="text-xs text-slate-500">{fmtDate(i.created_at)}</div>
            </div>
            <div className={`flex items-center gap-1 text-sm font-bold tabular-nums ${pos ? "text-[#0F8A7D]" : "text-red-600"}`}>
              {pos ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}{pos ? "+" : ""}{i.amount}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const statusFor = (key, s) => {
  if (key === "daily_login") return s.daily_login_earned ? "Earned today" : null;
  if (key === "profile_complete") return s.profile_complete_earned ? "Completed" : null;
  if (key === "weekly_bonus") return s.weekly_bonus_earned ? "Earned this week" : `${s.week_actions}/${s.weekly_threshold} this week`;
  return null;
};

const EarnTab = ({ s }) => {
  const [rules, setRules] = useState([]);
  useEffect(() => { api.get("/dotcoins/rules").then((r) => setRules(r.data.filter((x) => x.enabled))); }, []);
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="dotcoins-earn-list">
      {rules.map((r) => {
        const Icon = RULE_ICONS[r.key] || DotCoinIcon;
        const st = statusFor(r.key, s);
        return (
          <div key={r.key} className="group flex gap-4 rounded-xl border border-slate-200 bg-white p-5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#14B8A6]" data-testid={`dotcoins-rule-${r.key}`}>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#0F2A5F] text-white"><Icon size={18} /></div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-semibold text-slate-800">{r.label}</div>
                <div className="flex shrink-0 items-center gap-1 text-sm font-bold text-[#0F8A7D]">+{r.amount} <DotCoinIcon size={14} /></div>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">{r.description}</p>
              {st && <div className="mt-2 inline-block rounded bg-[#14B8A6]/10 px-2 py-0.5 text-[11px] font-medium text-[#0F8A7D]">{st}</div>}
              {r.key === "profile_complete" && !s.profile_complete_earned && s.profile_missing_fields.length > 0 && (
                <div className="mt-2 text-[11px] text-amber-700">Missing: {s.profile_missing_fields.map((f) => FIELD_LABELS[f] || f).join(", ")} — update via Clinic Details.</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function DotCoins() {
  const [s, setS] = useState(null);
  const { balance, refresh } = useDotCoins();
  const load = useCallback(() => api.get("/dotcoins").then((r) => setS(r.data)), []);
  useEffect(() => { load(); refresh(); }, [load, refresh]);
  useEffect(() => { if (balance !== null) load(); }, [balance, load]);

  return (
    <div className="mx-auto max-w-6xl px-8 py-10" data-testid="dotcoins-page">
      <div className="mb-8 flex items-center gap-3">
        <DotCoinIcon size={40} />
        <div>
          <h1 className="text-3xl font-bold text-[#0F2A5F]">DotCoins</h1>
          <p className="text-sm text-slate-500">Earn rewards for using DotMedy every day. <Link to="/app/patients" className="text-[#14B8A6] hover:underline">Add a patient</Link> to start.</p>
        </div>
      </div>
      {!s ? (
        <div className="p-8 text-center text-sm text-slate-400">Loading...</div>
      ) : (
        <Tabs defaultValue="balance">
          <TabsList className="mb-6">
            <TabsTrigger value="balance" data-testid="dotcoins-tab-balance">Balance</TabsTrigger>
            <TabsTrigger value="history" data-testid="dotcoins-tab-history">History</TabsTrigger>
            <TabsTrigger value="earn" data-testid="dotcoins-tab-earn">Earn</TabsTrigger>
          </TabsList>
          <TabsContent value="balance"><BalanceTab s={s} /></TabsContent>
          <TabsContent value="history"><HistoryTab /></TabsContent>
          <TabsContent value="earn"><EarnTab s={s} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
